import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { getMaterialInfo } from '../../utils/helpers';
import { ChatInput, ChatCard, MaterialCard, UpgradePrompt } from '../../components/features';
import ChatView from '../ChatView';

/**
 * Calculator Page Component
 * 
 * Material-specific landing page with tailored content and examples
 */
const CalculatorPage = ({ type }) => {
  const { user } = useAuth();
  const { 
    chats, 
    isLoading, 
    getRemainingCalcs, 
    canCalculate, 
    sendMessage, 
    deleteChat,
    activeChat,
    setActiveChat,
    isPro
  } = useChat();
  const navigate = useNavigate();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const materialInfo = getMaterialInfo(type);
  const remainingCalcs = getRemainingCalcs();

  // Filter chats by material type
  const materialChats = chats.filter(chat => chat.material_type === type);

  // Material-specific content
  const materialContent = {
    concrete: {
      title: 'Concrete Calculator',
      headline: 'Calculate Concrete Materials with AI',
      description: 'Get accurate estimates for concrete slabs, footings, and foundations. Calculate cubic yards, bags, rebar, and more.',
      examples: [
        '10x12 patio slab, 4 inches thick',
        '20x24 garage foundation with footings',
        'Concrete driveway 50ft x 12ft',
        'Steps 4ft wide, 3 risers',
      ],
      tips: [
        'Standard thickness for patios is 4 inches',
        'Add 10% for waste and spillage',
        'Use rebar for driveways and high-traffic areas',
        'One cubic yard covers about 80 sq ft at 4" depth',
      ],
      useCases: [
        'Patios & Walkways',
        'Driveways',
        'Garage Floors',
        'Foundations',
        'Footings',
        'Steps & Stairs',
      ],
    },
    drywall: {
      title: 'Drywall Calculator',
      headline: 'Calculate Drywall Materials with AI',
      description: 'Estimate drywall sheets, mud, tape, and screws for any room. Get accurate counts for walls and ceilings.',
      examples: [
        '12x14 bedroom with 9ft ceilings',
        'Finish basement 800 sq ft',
        'Ceiling only, 15x20 room',
        'Repair 4x8 section of wall',
      ],
      tips: [
        'Standard sheets are 4x8 (32 sq ft)',
        'Add 10-15% for waste and cuts',
        'Use moisture-resistant in bathrooms',
        '1 gallon of mud covers ~100 sq ft',
      ],
      useCases: [
        'New Construction',
        'Room Additions',
        'Basement Finishing',
        'Garage Conversion',
        'Ceiling Repairs',
        'Wall Repairs',
      ],
    },
    paint: {
      title: 'Paint Calculator',
      headline: 'Calculate Paint Materials with AI',
      description: 'Figure out exactly how much paint you need. Includes primer, supplies, and coverage estimates.',
      examples: [
        'Paint 3 bedrooms, 12x12 each',
        'Exterior of 2-story house',
        'Kitchen cabinets, 20 doors',
        'Accent wall 15ft wide, 10ft tall',
      ],
      tips: [
        'One gallon covers 350-400 sq ft',
        'Plan for 2 coats minimum',
        'Prime bare drywall and dark colors',
        'Subtract windows and doors from total',
      ],
      useCases: [
        'Interior Walls',
        'Ceilings',
        'Exterior Siding',
        'Trim & Doors',
        'Cabinets',
        'Decks & Fences',
      ],
    },
    flooring: {
      title: 'Flooring Calculator',
      headline: 'Calculate Flooring Materials with AI',
      description: 'Estimate flooring for tile, hardwood, vinyl, laminate, and carpet. Includes underlayment and trim.',
      examples: [
        'Hardwood for 500 sq ft living room',
        'Tile bathroom 8x10 with shower',
        'Vinyl plank for whole house 1800 sq ft',
        'Carpet 3 bedrooms, 12x12 each',
      ],
      tips: [
        'Add 10% waste for straight patterns',
        'Add 15% for diagonal installation',
        'Include transitions between rooms',
        'Acclimate hardwood 48-72 hours',
      ],
      useCases: [
        'Hardwood Floors',
        'Tile Flooring',
        'Vinyl Plank',
        'Laminate',
        'Carpet',
        'Bathroom Tile',
      ],
    },
    lumber: {
      title: 'Lumber Calculator',
      headline: 'Calculate Lumber Materials with AI',
      description: 'Get lumber lists for framing, decking, and trim. Calculate studs, joists, and board feet.',
      examples: [
        'Frame 12x16 shed with door',
        'Deck 16x20 with stairs and railing',
        'Fence 100 linear feet, 6ft tall',
        'Stud wall 20ft long, 8ft ceiling',
      ],
      tips: [
        'Studs typically 16" on center',
        'Headers needed above doors/windows',
        'Use pressure-treated for ground contact',
        'Check local codes for requirements',
      ],
      useCases: [
        'Wall Framing',
        'Deck Building',
        'Fence Construction',
        'Shed Building',
        'Trim & Molding',
        'Shelving',
      ],
    },
    roofing: {
      title: 'Roofing Calculator',
      headline: 'Calculate Roofing Materials with AI',
      description: 'Estimate shingles, underlayment, and accessories. Accounts for roof pitch and waste.',
      examples: [
        'Roof 30x40 house, 6/12 pitch',
        'Garage roof 24x24, hip style',
        'Replace shingles on 1500 sq ft roof',
        'Shed roof 8x12, lean-to style',
      ],
      tips: [
        '1 square = 100 sq ft = 3 bundles',
        'Add 10-15% for waste and hips',
        'Pitch factor increases material needs',
        'Include ridge caps and starter strips',
      ],
      useCases: [
        'Asphalt Shingles',
        'Metal Roofing',
        'Roof Repair',
        'Shed Roofs',
        'Garage Roofs',
        'New Construction',
      ],
    },
  };

  const content = materialContent[type] || materialContent.concrete;

  // Other materials for sidebar
  const otherMaterials = ['concrete', 'drywall', 'paint', 'flooring', 'lumber', 'roofing']
    .filter(m => m !== type);

  const handleSendMessage = async (input) => {
    if (!canCalculate()) {
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const newChat = await sendMessage(input, false, null, type);
      if (newChat) {
        setActiveChat(newChat);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleOpenChat = (chat) => {
    if (!user) {
      navigate('/signin');
      return;
    }
    setActiveChat(chat);
  };

  const handleDeleteChat = async (chatId) => {
    await deleteChat(chatId);
  };

  // Show chat view if there's an active chat
  if (activeChat) {
    return <ChatView />;
  }

  return (
    <>
      <Helmet>
        <title>{content.title} - CalcBuildAI</title>
        <meta name="description" content={content.description} />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-500">
              <Link to="/" className="hover:text-gray-700">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/" className="hover:text-gray-700">Calculators</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900 font-medium">{materialInfo.name}</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className={`py-12 ${materialInfo.color}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="text-5xl mb-4">{materialInfo.icon}</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {content.headline}
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {content.description}
            </p>

            {/* Chat Input */}
            <div className="max-w-2xl mx-auto mb-6">
              <ChatInput
                onSend={handleSendMessage}
                isLoading={isLoading}
                placeholder={`Describe your ${type} project...`}
                examples={content.examples}
              />
            </div>

            {/* Usage indicator */}
            <div className="flex justify-center">
              {isPro ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">✨ Pro - Unlimited calculations</span>
                </div>
              ) : remainingCalcs > 0 ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-green-700 rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">🎁 {remainingCalcs} free calculation{remainingCalcs !== 1 ? 's' : ''} remaining</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-red-700 rounded-lg shadow-sm">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">You've used all free calculations</span>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="ml-2 underline hover:no-underline"
                  >
                    Upgrade
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Recent calculations for this material */}
              {materialChats.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Your {materialInfo.name} Calculations
                  </h2>
                  <div className="space-y-4">
                    {materialChats.slice(0, 3).map((chat) => (
                      <ChatCard
                        key={chat.id}
                        chat={chat}
                        onOpen={handleOpenChat}
                        onDelete={handleDeleteChat}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Use cases */}
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Common {materialInfo.name} Projects
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {content.useCases.map((useCase, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:border-blue-300 transition-colors"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{useCase}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Pro tips */}
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Pro Tips
                </h2>
                <div className="bg-blue-50 rounded-xl p-6">
                  <ul className="space-y-3">
                    {content.tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-blue-500 font-bold">💡</span>
                        <span className="text-gray-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Other calculators */}
              <div className="bg-white rounded-xl p-6 border">
                <h3 className="font-bold text-gray-900 mb-4">
                  Other Calculators
                </h3>
                <div className="space-y-3">
                  {otherMaterials.map((material) => (
                    <MaterialCard
                      key={material}
                      type={material}
                      variant="compact"
                    />
                  ))}
                </div>
              </div>

              {/* CTA for free users */}
              {!isPro && (
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
                  <h3 className="font-bold text-lg mb-2">
                    Upgrade to Pro
                  </h3>
                  <p className="text-blue-100 text-sm mb-4">
                    Get unlimited calculations, PDF exports, and no ads.
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="w-full py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                  >
                    View Pricing →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        reason="limit"
      />
    </>
  );
};

export default CalculatorPage;
